import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Grid, Icon, IconButton,
    InputLabel, Skeleton, Switch, Table, TableBody, TableCell, TableHead, TableRow, Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDatePicker from "components/MDDatePicker";
import MDInput from "components/MDInput";
import MDPeriodo from "components/MDPeriodo";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { coresDoTema, TEXTO_COLORIDO_ESCURO } from "assets/coresTema";
import { MySwal } from "App";
import { errorAlert } from "utils/Functions";
import { get, post } from "utils/WebService";
import MotivosAfastamentoDialog from "./MotivosAfastamentoDialog";

const dataBr = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");

// "YYYY-MM-DD" <-> data local (sem toISOString, que vira UTC e grava o dia anterior)
const paraData = (iso) => {
    if (!iso) return null;
    const [ano, mes, dia] = iso.slice(0, 10).split("-").map(Number);
    return new Date(ano, mes - 1, dia);
};
const paraIso = (d) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "");

const periodoDe = (a) => (a.indeterminado ? `A partir de ${dataBr(a.inicio)}, sem data de fim` : `${dataBr(a.inicio)} a ${dataBr(a.fim)}`);

const SITUACOES = {
    EM_ANDAMENTO: { rotulo: "Afastado agora", cor: "warning" },
    FUTURO: { rotulo: "Agendado", cor: "info" },
    ENCERRADO: { rotulo: "Encerrado", cor: "default" },
};

/**
 * Afastamentos do usuário (férias, licença...): na Mala Direta que sai pelo e-mail do responsável, as fichas dele saem
 * pelas caixas dos outros responsáveis do lote enquanto durar o afastamento (a ficha continua dele). O motivo vem da
 * lista de motivos (cadastrável aqui mesmo) e o afastamento pode ficar sem data de fim até alguém informar a volta.
 * Grava na hora, fora do "Salvar" do usuário; incluir, alterar e excluir exigem Usuários > Editar.
 */
const AfastamentosUsuario = ({ userId }) => {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);
    const [carregando, setCarregando] = useState(true);
    const [lista, setLista] = useState([]);
    const [motivos, setMotivos] = useState([]);
    const [podeAlterar, setPodeAlterar] = useState(false);
    const [periodo, setPeriodo] = useState({ inicio: "", fim: "" });
    const [indeterminado, setIndeterminado] = useState(false);
    const [motivoId, setMotivoId] = useState(null);
    const [observacao, setObservacao] = useState("");
    const [salvando, setSalvando] = useState(false);
    const [criandoMotivo, setCriandoMotivo] = useState(false);
    const [gerirMotivos, setGerirMotivos] = useState(false);
    // Alterar o último dia: { afastamento, fim, indeterminado }
    const [fimEmEdicao, setFimEmEdicao] = useState(null);
    const [salvandoFim, setSalvandoFim] = useState(false);

    const aplicar = (r) => {
        setLista(r?.afastamentos || []);
        if (r?.motivos) setMotivos(r.motivos);
        setPodeAlterar(!!r?.podeAlterar);
    };

    useEffect(() => {
        if (!userId) return;
        setCarregando(true);
        get(`/users/afastamentos/${userId}`)
            .then(aplicar)
            .catch((err) => errorAlert(err?.message || "Erro ao carregar os afastamentos."))
            .finally(() => setCarregando(false));
    }, [userId]);

    // O motivo escolhido que foi inativado ou excluído no cadastro sai da escolha
    const motivosAtivos = motivos.filter((m) => m.ativo);
    useEffect(() => {
        if (motivoId && !motivosAtivos.some((m) => m.id === motivoId)) setMotivoId(null);
    }, [motivos]); // eslint-disable-line react-hooks/exhaustive-deps

    const criarMotivo = (nome) => {
        setCriandoMotivo(true);
        post("/users/afastamentos/motivos/salvar", { nome })
            .then((r) => {
                const novos = r?.motivos || [];
                setMotivos(novos);
                const criado = novos.find((m) => m.nome.toLowerCase() === nome.trim().replace(/\s+/g, " ").toLowerCase());
                if (criado) setMotivoId(criado.id);
            })
            .catch((err) => errorAlert(err?.message || "Erro ao criar o motivo."))
            .finally(() => setCriandoMotivo(false));
    };

    const incluir = () => {
        setSalvando(true);
        post("/users/afastamentos/salvar", {
            idUsuario: userId,
            inicio: periodo.inicio,
            fim: indeterminado ? null : periodo.fim || periodo.inicio,
            indeterminado,
            idMotivo: motivoId,
            observacao,
        })
            .then((r) => {
                aplicar(r);
                setPeriodo({ inicio: "", fim: "" });
                setIndeterminado(false);
                setMotivoId(null);
                setObservacao("");
            })
            .catch((err) => errorAlert(err?.message || "Erro ao salvar o afastamento."))
            .finally(() => setSalvando(false));
    };

    const salvarFim = () => {
        setSalvandoFim(true);
        post("/users/afastamentos/alterar-fim", {
            id: fimEmEdicao.afastamento.id,
            fim: fimEmEdicao.indeterminado ? null : fimEmEdicao.fim,
            indeterminado: fimEmEdicao.indeterminado,
        })
            .then((r) => {
                aplicar(r);
                setFimEmEdicao(null);
            })
            .catch((err) => errorAlert(err?.message || "Erro ao alterar o afastamento."))
            .finally(() => setSalvandoFim(false));
    };

    const excluir = (a) => {
        MySwal.fire({
            title: "Excluir o afastamento?",
            text: `${periodoDe(a)}${a.motivo ? ` (${a.motivo})` : ""}. O histórico do usuário guarda a exclusão.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Excluir",
            cancelButtonText: "Cancelar",
        }).then((res) => {
            if (!res.isConfirmed) return;
            post("/users/afastamentos/excluir", { id: a.id })
                .then(aplicar)
                .catch((err) => errorAlert(err?.message || "Erro ao excluir o afastamento."));
        });
    };

    const corChip = (cor) => {
        if (cor === "default") return { color: cores.textoSecundario, borderColor: cores.bordaForte };
        return darkMode ? { color: TEXTO_COLORIDO_ESCURO[cor], borderColor: TEXTO_COLORIDO_ESCURO[cor] } : {};
    };

    const opcaoMotivo = motivosAtivos.find((m) => m.id === motivoId) || null;

    return (
        <MDBox px={3} pb={2}>
            <Divider sx={{ my: 1 }} />
            <MDTypography variant="button" fontWeight="bold">Afastamentos</MDTypography>
            <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1 }}>
                Férias, licença, folga... Enquanto durar, a Mala Direta que sai pelo e-mail do responsável manda as fichas dele pelas
                caixas dos outros responsáveis do lote (a ficha continua dele). Grava na hora, sem o botão Salvar.
            </MDTypography>

            {podeAlterar && (
                <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <MDPeriodo
                            label={indeterminado ? "Início" : "Período"}
                            inicio={periodo.inicio}
                            fim={indeterminado ? "" : periodo.fim}
                            fimAberto={indeterminado}
                            onChange={({ inicio, fim }) => setPeriodo({ inicio, fim: indeterminado ? "" : fim })}
                        />
                        <FormControlLabel
                            sx={{ ml: 0.5, mt: 0.5 }}
                            control={(
                                <Switch
                                    size="small"
                                    checked={indeterminado}
                                    onChange={(e) => {
                                        setIndeterminado(e.target.checked);
                                        if (e.target.checked) setPeriodo((p) => ({ inicio: p.inicio, fim: "" }));
                                    }}
                                />
                            )}
                            label={<MDTypography variant="caption" color="text">Sem data de fim (tempo indeterminado)</MDTypography>}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MDBox display="flex" alignItems="center" justifyContent="space-between">
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Motivo *</InputLabel>
                            <Tooltip title="Cadastrar, renomear, inativar ou excluir motivos">
                                <MDTypography
                                    component="span"
                                    variant="caption"
                                    color="info"
                                    sx={{ cursor: "pointer", pr: 1, pb: 0.5 }}
                                    onClick={() => setGerirMotivos(true)}
                                >
                                    Motivos
                                </MDTypography>
                            </Tooltip>
                        </MDBox>
                        <MDSelect
                            creatable
                            isClearable
                            isLoading={criandoMotivo}
                            options={motivosAtivos}
                            getOptionValue={(m) => m.id}
                            getOptionLabel={(m) => m.label || m.nome}
                            value={opcaoMotivo}
                            onChange={(m) => setMotivoId(m ? m.id : null)}
                            onCreateOption={criarMotivo}
                            formatCreateLabel={(texto) => `Criar o motivo "${texto}"`}
                            isValidNewOption={(texto) => !!texto.trim()
                                && !motivos.some((m) => m.nome.toLowerCase() === texto.trim().replace(/\s+/g, " ").toLowerCase())}
                            noOptionsMessage={() => "Nenhum motivo: digite para criar"}
                            placeholder="Escolha ou digite para criar"
                        />
                    </Grid>
                    <Grid item xs={12} sm={8} md={4}>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Observação</InputLabel>
                        <MDInput
                            size="small"
                            fullWidth
                            value={observacao}
                            inputProps={{ maxLength: 200 }}
                            placeholder="Opcional"
                            onChange={(e) => setObservacao(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>&nbsp;</InputLabel>
                        <MDButton
                            variant="contained"
                            color="info"
                            size="medium"
                            fullWidth
                            sx={{ height: 38 }}
                            disabled={!periodo.inicio || !motivoId || salvando}
                            onClick={incluir}
                            endIcon={salvando ? <CircularProgress color="inherit" size={16} /> : null}
                        >
                            Incluir
                        </MDButton>
                    </Grid>
                </Grid>
            )}

            {carregando ? (
                <Skeleton height={60} />
            ) : lista.length === 0 ? (
                <MDTypography variant="caption" color="text">Nenhum afastamento cadastrado.</MDTypography>
            ) : (
                <Table size="small">
                    <TableHead sx={{ display: "table-header-group" }}>
                        <TableRow>
                            {["Período", "Motivo", "Situação", "Incluído", ""].map((t) => (
                                <TableCell key={t} sx={{ fontSize: 12, fontWeight: "bold", color: cores.texto, backgroundColor: cores.painel }}>{t}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {lista.map((a) => {
                            const s = SITUACOES[a.situacao] || SITUACOES.ENCERRADO;
                            return (
                                <TableRow key={a.id}>
                                    <TableCell sx={{ fontSize: 12, color: cores.texto }}>{periodoDe(a)}</TableCell>
                                    <TableCell sx={{ fontSize: 12, color: cores.texto }}>
                                        {a.motivo || (a.observacao ? "" : "-")}
                                        {a.observacao && (
                                            <MDBox component="span" sx={{ display: "block", color: cores.textoSecundario }}>{a.observacao}</MDBox>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" variant="outlined" color={s.cor} label={s.rotulo} sx={corChip(s.cor)} />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 12, color: cores.textoSecundario }}>
                                        {a.incluidoEm}{a.incluidoPor ? ` por ${a.incluidoPor}` : ""}
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                        {podeAlterar && (
                                            <>
                                                <Tooltip title={a.indeterminado ? "Informar a volta (último dia do afastamento)" : "Alterar o último dia"}>
                                                    <IconButton
                                                        size="small"
                                                        color="info"
                                                        onClick={() => setFimEmEdicao({ afastamento: a, fim: a.fim || "", indeterminado: !!a.indeterminado })}
                                                    >
                                                        <Icon fontSize="small">event_available</Icon>
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Excluir afastamento">
                                                    <IconButton size="small" color="error" onClick={() => excluir(a)}>
                                                        <Icon fontSize="small">delete</Icon>
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}

            <Dialog open={!!fimEmEdicao} onClose={() => !salvandoFim && setFimEmEdicao(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Fim do afastamento</DialogTitle>
                {fimEmEdicao && (
                    <DialogContent>
                        <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1.5 }}>
                            {periodoDe(fimEmEdicao.afastamento)}{fimEmEdicao.afastamento.motivo ? ` (${fimEmEdicao.afastamento.motivo})` : ""}.
                            {" "}Informe o último dia afastado (os dois dias do período valem).
                        </MDTypography>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Último dia do afastamento</InputLabel>
                        <MDDatePicker
                            value={fimEmEdicao.indeterminado ? null : paraData(fimEmEdicao.fim)}
                            onChange={(datas) => setFimEmEdicao((f) => ({ ...f, fim: paraIso(datas?.[0]), indeterminado: false }))}
                            options={{ dateFormat: "d/m/Y", minDate: paraData(fimEmEdicao.afastamento.inicio), disableMobile: true }}
                            input={{ size: "small", fullWidth: true, placeholder: "dd/mm/aaaa" }}
                        />
                        <FormControlLabel
                            sx={{ ml: 0.5, mt: 0.5 }}
                            control={(
                                <Switch
                                    size="small"
                                    checked={fimEmEdicao.indeterminado}
                                    onChange={(e) => setFimEmEdicao((f) => ({ ...f, indeterminado: e.target.checked }))}
                                />
                            )}
                            label={<MDTypography variant="caption" color="text">Sem data de fim (tempo indeterminado)</MDTypography>}
                        />
                    </DialogContent>
                )}
                <DialogActions>
                    <MDButton variant="text" color="secondary" disabled={salvandoFim} onClick={() => setFimEmEdicao(null)}>Cancelar</MDButton>
                    <MDButton
                        variant="contained"
                        color="info"
                        disabled={salvandoFim || (!fimEmEdicao?.indeterminado && !fimEmEdicao?.fim)}
                        onClick={salvarFim}
                        endIcon={salvandoFim ? <CircularProgress color="inherit" size={16} /> : null}
                    >
                        Salvar
                    </MDButton>
                </DialogActions>
            </Dialog>

            <MotivosAfastamentoDialog
                open={gerirMotivos}
                motivos={motivos}
                onChange={setMotivos}
                onClose={() => setGerirMotivos(false)}
            />
        </MDBox>
    );
};

AfastamentosUsuario.propTypes = {
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default AfastamentosUsuario;
